import React, { createContext, useContext, useMemo, useState, useLayoutEffect, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import type { DependencyList, ReactNode, Dispatch, SetStateAction } from "react";
import { PageContainer, type PageContainerProps, type PageContainerWidth } from "./PageContainer";

export type PageShellOptions = Omit<PageContainerProps, "children"> & {
  enabled?: boolean;
};

interface PageShellContextValue {
  options: Required<PageShellOptions>;
  setOptions: Dispatch<SetStateAction<Required<PageShellOptions>>>;
  reset: () => void;
}

export const DEFAULT_PAGE_SHELL_OPTIONS: Required<PageShellOptions> = {
  enabled: true,
  width: "wide",
  bleed: false,
  disablePadding: false,
  disableBottomPadding: false,
  className: "",
};

const PageShellContext = createContext<PageShellContextValue | null>(null);

function mergeOptions(
  options?: Partial<PageShellOptions>,
  base: Required<PageShellOptions> = DEFAULT_PAGE_SHELL_OPTIONS
): Required<PageShellOptions> {
  const merged = {
    ...base,
    ...options,
    enabled: options?.enabled ?? base.enabled,
    width: options?.width ?? (base.width as PageContainerWidth),
  };

  return merged;
}

interface PageShellProps {
  children: ReactNode;
  defaultOptions?: Partial<PageShellOptions>;
}

export function PageShell({ children, defaultOptions }: PageShellProps) {
  const location = useLocation();
  const mergedDefaults = useMemo(() => mergeOptions(defaultOptions), [defaultOptions]);
  const [options, setOptions] = useState<Required<PageShellOptions>>(mergedDefaults);

  const reset = useCallback(() => {
    setOptions(mergedDefaults);
  }, [mergedDefaults]);

  useEffect(() => {
    reset();
  }, [location.pathname, reset]);

  const contextValue = useMemo<PageShellContextValue>(
    () => ({
      options,
      setOptions,
      reset,
    }),
    [options, reset]
  );

  const content =
    options.enabled !== false ? (
      <PageContainer
        width={options.width}
        bleed={options.bleed}
        disablePadding={options.disablePadding}
        disableBottomPadding={options.disableBottomPadding}
        className={options.className}
      >
        {children}
      </PageContainer>
    ) : (
      children
    );

  return <PageShellContext.Provider value={contextValue}>{content}</PageShellContext.Provider>;
}

export function usePageShell(options?: Partial<PageShellOptions>, deps: DependencyList = []) {
  const context = useContext(PageShellContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOptions = useMemo(() => options, deps);

  useLayoutEffect(() => {
    if (!context || !stableOptions) return;

    context.setOptions((prev) => {
      const merged = mergeOptions(stableOptions, prev);
      // Only update if the merged options are actually different
      if (
        merged.enabled === prev.enabled &&
        merged.width === prev.width &&
        merged.bleed === prev.bleed &&
        merged.disablePadding === prev.disablePadding &&
        merged.disableBottomPadding === prev.disableBottomPadding &&
        merged.className === prev.className
      ) {
        return prev; // Return the same reference to prevent re-renders
      }
      return merged;
    });
  }, [context, stableOptions]);

  return {
    setOptions: context?.setOptions,
    reset: context?.reset,
    options: context?.options ?? DEFAULT_PAGE_SHELL_OPTIONS,
  };
}

